class Anniversary10thHero extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.track = this.querySelector('[data-hero-track]');
    this.stage = this.querySelector('[data-hero-stage]');
    this.slides = [...this.querySelectorAll('[data-hero-slide]')];
    this.gsap = window.gsap;
    this.ScrollTrigger = window.ScrollTrigger;

    if (!this.track || !this.stage || this.slides.length < 2 || !this.gsap || !this.ScrollTrigger) return;

    this.isInitialized = true;
    this.current = 0;
    this.timeline = null;
    this.scrollTrigger = null;
    this.reduceMotion = false;

    this.handleBlockSelect = this.handleBlockSelect.bind(this);
    this.handleWindowLoad = this.handleWindowLoad.bind(this);

    this.gsap.registerPlugin(this.ScrollTrigger);
    this.classList.add('is-enhanced');

    this.gsapContext = this.gsap.context(() => {
      this.gsapMatchMedia = this.gsap.matchMedia();
      this.gsapMatchMedia.add('(prefers-reduced-motion: no-preference)', () => this.setupProgress(false));
      this.gsapMatchMedia.add('(prefers-reduced-motion: reduce)', () => this.setupProgress(true));
    }, this);

    document.addEventListener('shopify:block:select', this.handleBlockSelect);

    if (document.readyState === 'complete') {
      this.ScrollTrigger.refresh();
    } else {
      window.addEventListener('load', this.handleWindowLoad, { once: true });
    }
  }

  // 固定由 CSS sticky 完成，这里只读取舞台停留区间的滚动进度，不写入任何尺寸内联样式
  setupProgress(reduceMotion) {
    const lastIndex = this.slides.length - 1;
    this.reduceMotion = reduceMotion;

    this.scrollTrigger = this.ScrollTrigger.create({
      trigger: this.track,
      start: 'top top',
      end: () => `+=${Math.max(1, this.track.offsetHeight - this.stage.offsetHeight)}`,
      invalidateOnRefresh: true,
      onUpdate: (self) => this.goTo(Math.round(self.progress * lastIndex)),
      onRefresh: (self) => this.goTo(Math.round(self.progress * lastIndex), true)
    });

    this.goTo(Math.round(this.scrollTrigger.progress * lastIndex), true);

    return () => {
      this.timeline?.kill();
      this.timeline = null;
      this.scrollTrigger?.kill();
      this.scrollTrigger = null;
    };
  }

  goTo(index, immediate = false) {
    if (index < 0 || index >= this.slides.length) return;
    if (index === this.current && !immediate) return;

    const direction = index >= this.current ? 1 : -1;
    this.current = index;
    this.timeline?.kill();
    this.timeline = null;

    if (immediate || this.reduceMotion) {
      this.slides.forEach((slide, slideIndex) => {
        this.gsap.set(slide, { opacity: slideIndex === index ? 1 : 0, y: 0 });
      });
      return;
    }

    // 位移按舞台高度换算，跟随 --content-ratio 缩放
    const offset = this.stage.offsetHeight * 0.03;
    this.timeline = this.gsap.timeline({ defaults: { overwrite: 'auto' } });

    this.slides.forEach((slide, slideIndex) => {
      if (slideIndex === index) return;

      this.timeline.to(
        slide,
        { opacity: 0, y: -offset * direction, duration: 0.3, ease: 'power2.in' },
        0
      );
    });

    this.timeline.fromTo(
      this.slides[index],
      { opacity: 0, y: offset * direction },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' },
      0.2
    );
  }

  handleBlockSelect(event) {
    const slide = event.target instanceof Element ? event.target.closest('[data-hero-slide]') : null;
    if (!slide || !this.contains(slide)) return;

    const index = this.slides.indexOf(slide);
    if (index < 0) return;

    if (this.scrollTrigger) {
      const { start, end } = this.scrollTrigger;
      window.scrollTo({ top: start + (end - start) * (index / (this.slides.length - 1)), behavior: 'auto' });
    }

    this.goTo(index, true);
  }

  handleWindowLoad() {
    this.ScrollTrigger?.refresh();
  }

  disconnectedCallback() {
    if (!this.isInitialized) return;

    document.removeEventListener('shopify:block:select', this.handleBlockSelect);
    window.removeEventListener('load', this.handleWindowLoad);

    this.timeline?.kill();
    this.scrollTrigger?.kill();
    this.gsapMatchMedia?.revert();
    this.gsapContext?.revert();
    this.classList.remove('is-enhanced');
    this.ScrollTrigger?.refresh();

    this.timeline = null;
    this.scrollTrigger = null;
    this.gsapMatchMedia = null;
    this.gsapContext = null;
    this.isInitialized = false;
  }
}

if (!customElements.get('anniversary-10th-hero')) {
  customElements.define('anniversary-10th-hero', Anniversary10thHero);
}
